<template>
  <master-layout pageTitle="Message Templates">
    <div v-if="whatsappConnected">
      <div class="search-template-list">
        <SearchBar
          v-model="templateFilter"
          placeholder="Search message templates"
          :rightIcon="true"
          @search="onSearchTemplate"
          class="template-search-field"
        />
        <div class="desktop-only">
          <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Create New" @click="() => $router.push('/sales-channel/whatsapp/add-template')"></VButton>
        </div>
        <div class="mobile-only">
          <ActionPopOver :style="{'--width': '177px'}">
            <div class="listContainer">
              <button class="popover-item" @click="() => $router.push('/sales-channel/whatsapp/add-template')">Create New</button>
            </div>
          </ActionPopOver>
        </div>
      </div>

      <div class="gridMargin">
        <div class="centered-message-container" v-if="isLoading">
          <img src="../../../resources/images/not-found.svg" alt="not_found" class="filterImg" />
          <h5>Fetching templates...</h5>
        </div>
        <template v-else>
          <!-- Table Header -->
          <div class="segment-list" v-if="templatesToShow.length > 0">
            <div class="template-table-header">
              <div class="col-name"><span class="vtext-subtitle">Name</span></div>
              <div class="col-category desktop-only"><span class="vtext-subtitle">Category</span></div>
              <div class="col-language desktop-only"><span class="vtext-subtitle">Language</span></div>
              <div class="col-status desktop-only"><span class="vtext-subtitle">Status</span></div>
              <div class="col-actions text-right"><span class="vtext-subtitle">Actions</span></div>
            </div>
          </div>

          <!-- Table Body -->
          <div class="segment-info" v-if="templatesToShow.length > 0">
            <div v-for="(template, tIndex) in templatesToShow" :key="tIndex" class="template-row rowBorder">
              <div class="col-name">
                <span class="vtext-body template-name">{{ template.template_name }}</span>
              </div>
              <div class="col-category desktop-only">
                <span class="vtext-body">{{ formatText(template.category) || 'N/A' }}</span>
              </div>
              <div class="col-language desktop-only">
                <span class="vtext-body" v-if="template.language">{{ getLocaleName(template.language, 'whatsapp') }}</span>
                <span class="vtext-body" v-else>N/A</span>
              </div>
              <div class="col-status desktop-only" v-if="template.status">
                <div>
                  <span class="status" :class="statusClass(template.status.toLowerCase())"></span>
                  <span class="vtext-body">{{ getStatusLabel(template.status.toLowerCase()) }}</span>
                </div>
                <!-- Reject reason -->
                <VBadge v-if="!['pending', 'approved', 'deleted'].includes(template.status.toLowerCase()) && template.rejected_reason && template.rejected_reason !== 'None'" appearance="filled" connotation="announcement" :text="template.rejected_reason" class="secondary-reason"></VBadge>
              </div>
              <div class="col-status desktop-only" v-else>
                <span class="vtext-body">Not Available</span>
              </div>
              <div class="col-actions desktop-only">
                <div class="actionFlexContainer">
                  <span v-if="['MARKETING', 'AUTHENTICATION', 'UTILITY'].includes(template.category) && ['approved', 'rejected', 'disabled', 'paused', 'limit_exceeded'].includes(template.status.toLowerCase())" class="tooltip_container">
                    <VIcon name="edit-line" class="actionIcons cursorPointer" @click="() => $router.push({ path: '/sales-channel/whatsapp/add-template' + (template?.id ? ( '/' + template.id ) : '' ), query: { edit: true } })" />
                    <span class="tooltip_text">Edit</span>
                  </span>
                  <span v-else class="tooltip_container">
                    <VIcon name="eye-line" @click="() => $router.push({ path: '/sales-channel/whatsapp/add-template' + (template?.id ? ( '/' + template.id ) : '' ), query: { edit: true } })" class="actionIcons cursorPointer" />
                    <span class="tooltip_text">View</span>
                  </span>
                  <span class="tooltip_container">
                    <VIcon name="delete-line" connotation="alert" @click="() => templateIndexToDelete = tIndex" class="trashIcon actionIcons cursorPointer" />
                    <span class="tooltip_text">Delete</span>
                  </span>
                </div>
              </div>
              <!-- Mobile actions -->
              <div class="col-actions mobile-only">
                <ActionPopOver :style="{'--width': '215px'}">
                  <div class="listContainer">
                    <button v-if="['MARKETING', 'AUTHENTICATION', 'UTILITY'].includes(template.category) && ['approved', 'rejected', 'disabled', 'paused', 'limit_exceeded'].includes(template.status.toLowerCase())" class="popover-item" @click="() => $router.push({ path: '/sales-channel/whatsapp/add-template' + (template?.id ? ( '/' + template.id ) : '' ), query: { edit: true } })">Edit</button>
                    <button v-else class="popover-item" @click="() => $router.push({ path: '/sales-channel/whatsapp/add-template' + (template?.id ? ( '/' + template.id ) : '' ), query: { edit: true } })">View</button>
                    <button class="popover-item popover-item--danger" @click="() => templateIndexToDelete = tIndex">Delete</button>
                  </div>
                </ActionPopOver>
              </div>
            </div>
          </div>

          <div class="centered-message-container" v-if="templateFilter && templateFilter.length && templatesToShow.length === 0">
            <img src="../../../resources/images/not-found.svg" alt="not_found" class="filterImg" />
            <h5>No templates matching this filter! 😟 <a @click="() => templateFilter = undefined" class="txt_clr_primary cursorPointer">Clear filter</a></h5>
          </div>
          <div class="centered-message-container" v-else-if="!templateFilter && allTemplates.length == 0">
            <img src="../../../resources/images/not-found.svg" alt="not_found" class="filterImg" />
            <h5>No templates yet! 😟</h5>
          </div>
        </template>
      </div>

      <!-- Sticky Pagination -->
      <div class="sticky-pagination" v-if="!isLoading && formattedTotalTemplates > 0 && formattedTotalTemplates > pageSize">
        <pagination 
          class="pagination-wrapper" 
          :totalCount="formattedTotalTemplates" 
          :pageSize="pageSize"
          :currentPage="currentPaginationNumber" 
          @change="onPaginationChange"
        />
      </div>
    </div>
    <div v-else>
      <page-access-not-allowed></page-access-not-allowed>
    </div>

    <!-- Delete Confirmation Dialog -->
    <VDialog :open="templateIndexToDelete > -1" headline="Are you sure?" modal @close="templateIndexToDelete = -1">
      <template #body>
        <p class="vtext-body">You want to delete selected template?</p>
      </template>
      <template #action-items>
        <VButton class="vvd-btn-cancel" appearance="filled" label="Cancel" @click="templateIndexToDelete = -1"></VButton>
        <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Yes" @click="deleteTemplate"></VButton>
      </template>
    </VDialog>
  </master-layout>
</template>

<script>
import { getLocaleName } from '@/resources/js/all-locale-data';
import { statusClass, getStatusLabel } from '@/helpers/status.helpers';
import ActionPopOver from '@/components/ActionPopOver.vue';
import apiInterceptor from '@/shared/apiInterceptor';
import pagination from '@/components/pagination.vue';
import { useChatStore } from '@/stores/chat';
import { useUIStore } from '@/stores/ui';
import { useSocialChannels } from '@/composables/useSocialChannels';
import { environment } from '@/shared/env';
import SearchBar from '@/components/SearchBar.vue';

export default {
  setup() {
    const uiStore = useUIStore();
    const { whatsappConnected, whatsAppSocialData } = useSocialChannels();
    return { whatsappConnected, whatsAppSocialData, uiStore };
  },
  components: {
    ActionPopOver,
    SearchBar,
    pagination
  },
  data() {
    return {
      allTemplates: [],
      templateIndexToDelete: -1,
      templateFilter: undefined,
      filteredTotalTemplates: 0,
      currentPaginationNumber: 1,
      pageSize: 10,
      isSearching: false
    }
  },
  mounted(){
    document.addEventListener( "template-update", this.handleTemplateStatusUpdate, true );
  },
  watch: {
    templateFilter(val) {
      if (!val || val === '') {
        this.isSearching = false;
        this.currentPaginationNumber = 1;
        this.fetchTemplates({ pageNumber: 1 });
      }
    }
  },
  computed: {
    isLoading() { return this.uiStore.isLoading; },
    templatesToShow() {
      // Only show paginated templates if not searching, else show search results
      return this.allTemplates;
    },
    formattedTotalTemplates() {
      return this.filteredTotalTemplates;
    }
  },
  beforeUnmount() {
    document.removeEventListener( "template-update", this.handleTemplateStatusUpdate, true );
  },
  ionViewWillEnter() {
    this.templateFilter = undefined;
    this.isSearching = false;
    this.currentPaginationNumber = 1;
    this.fetchTemplates({ pageNumber: 1 });
  },
  methods: {
    getLocaleName,
    statusClass,
    getStatusLabel,
    onPaginationChange(e) {
      const pageNumber = e.page;
      this.currentPaginationNumber = pageNumber;
      if (this.isSearching && this.templateFilter && this.templateFilter.trim() !== '') {
        this.fetchSearchedTemplates(this.templateFilter, pageNumber);
      } else {
        this.fetchTemplates({ pageNumber });
      }
    },
    async fetchTemplates({ pageNumber }) {
      if (!this.whatsAppSocialData?.id) return;
      try {
        this.currentPaginationNumber = pageNumber || 1;
        let offsetValue = this.currentPaginationNumber - 1;
        let reqBody = {
          size: this.pageSize,
          offset: offsetValue,
          fields: 'category,status,created,language,rejected_reason'
        };
        useUIStore().updateLoading({ isLoading: true, message: 'Fetching templates...' });
        const params = { ...reqBody };
        const whatsappTemplateResponse = await apiInterceptor.get( environment("VUE_APP_API_URL") + "social/v1/whatsapp/" + this.whatsAppSocialData.id + '/template/search', { params } );
        this.filteredTotalTemplates = whatsappTemplateResponse?.count || 0;
        this.allTemplates = whatsappTemplateResponse?.templates || [];
      } catch (error) {
        useUIStore().updateToast({ open: true, message: error.message || "Something went wrong", type: "danger" })
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    async fetchSearchedTemplates(searchVal, pageNumber = 1){
      if (!this.whatsAppSocialData?.id) return;
      try {
        useUIStore().updateLoading({ isLoading: true, message: 'Searching templates...' });
        let offsetValue = pageNumber - 1;
        const params = {
          size: this.pageSize,
          offset: offsetValue,
          name: searchVal,
          fields: 'category,status,created,language,rejected_reason'
        };
        const response = await apiInterceptor.get(
          environment("VUE_APP_API_URL") + `social/v1/whatsapp/${this.whatsAppSocialData.id}/template/search`,
          { params }
        );
        this.filteredTotalTemplates = response.count || 0;
        this.allTemplates = response.templates || [];
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Failed to search templates",
          type: "danger"
        });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    async deleteTemplate() {
      if (!this.whatsAppSocialData?.id) return;
      useUIStore().updateLoading({ isLoading: true, message: 'Deleting template...' });
      try {
        let thisTemplateToDelete = this.templatesToShow[this.templateIndexToDelete];
        const templateIndexToRemove = this.templateIndexToDelete;
        this.templateIndexToDelete = -1;
        if(thisTemplateToDelete?.id) {
          await apiInterceptor.delete(environment("VUE_APP_API_URL") + "social/v1/whatsapp/" + this.whatsAppSocialData.id + '/template/' +  thisTemplateToDelete.id );
          
          // Remove the template from allTemplates array instead of refetching
          this.allTemplates.splice(templateIndexToRemove, 1);
          // Update the total count
          this.filteredTotalTemplates = Math.max(0, this.filteredTotalTemplates - 1);
          
          useUIStore().updateToast({
            open: true,
            message: "Template Deleted Successfully",
            type: "success"
          });
        } else {
          useUIStore().updateToast({
            open: true,
            message: "Something went wrong",
            type: "danger"
          });
        }
      } catch (error) {
        console.error("Exception", error);
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
        this.templateIndexToDelete = -1;
      }
    },
    formatText(value) {
      if (!value) return '';
      value = value.toString();
      let words = value.split('_');
      for (let i = 0; i < words.length; i++) {
        words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1).toLowerCase();
      }
      return words.join(' ');
    },
    onSearchTemplate() {
      if (this.templateFilter && this.templateFilter.trim() !== '') {
        this.isSearching = true;
        this.currentPaginationNumber = 1;
        this.fetchSearchedTemplates(this.templateFilter, 1);
      } else if (this.isSearching || this.currentPaginationNumber !== 1) {
        // Only refetch the unfiltered list if we're actually leaving a search
        // or a different page. Clicking search/pressing Enter on an already
        // empty field showing page 1 would otherwise fire a no-op API call.
        this.isSearching = false;
        this.currentPaginationNumber = 1;
        this.fetchTemplates({ pageNumber: 1 });
      }
    },
    handleTemplateStatusUpdate(e) {
      try {    
        const data = e.detail;
        if (!data) {
          return;
        }
        const templateId = data.id;
        if (!templateId) {
          console.warn('Template ID not found in update data:', data);
          return;
        } 
        const templateIndex = this.allTemplates.findIndex(template => 
          String(template.id) === String(templateId)
        );
        if (templateIndex !== -1) {
          const updatedTemplate = { ...this.allTemplates[templateIndex] };
          let hasChanges = false;
          
          // Update status if provided
          if (data.status && data.status !== updatedTemplate.status) {
            updatedTemplate.status = data.status;
            hasChanges = true;
          }
          
          // Update rejected_reason if provided (handle "NONE" as empty)
          if (data.rejected_reason !== undefined) {
            const newRejectedReason = data.rejected_reason === "NONE" ? null : data.rejected_reason;
            if (newRejectedReason !== updatedTemplate.rejected_reason) {
              updatedTemplate.rejected_reason = newRejectedReason;
              hasChanges = true;
            }
          }
          // Only update if there are actual changes
          if (hasChanges) {
            // Update the template in the array reactively
            this.allTemplates.splice(templateIndex, 1, updatedTemplate);
          }
        } else {
          console.warn(`Template with id ${templateId} not found in current list`);
        }
      } catch (error) {
        console.error('Error handling template status update:', error);
      }
    }
  }
}
</script>

<style scoped>
.search-template-list {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  margin: 20px;
  gap: 16px;
}
.vivid-input {
  background: var(--white);
  border-radius: 25px;
  min-height: 40px;
  width: 100%;
}
.template-search-field {
  width: 30%;
}
.segment-list {
  background: var(--white);
  padding: 20px;
  border-bottom: 1px solid var(--background-primary);
  border-radius: 5px 5px 0px 0px;
}
.segment-info {
  border-radius: 0px 0px 5px 5px;
  padding: 10px 20px 20px 20px;
  background: var(--white);
  max-height: calc(100dvh - 250px);
  overflow-y: auto;
  overflow-x: hidden;
}

/* Template table grid */
.template-table-header,
.template-row {
  display: grid;
  grid-template-columns: 3fr 3fr 2fr 2fr 2fr;
  align-items: center;
  gap: 10px;
}
.col-name { min-width: 0; }
.col-category { min-width: 0; }
.col-language { min-width: 0; }
.col-status { min-width: 0; }
.col-actions { min-width: 0; }
.text-right { text-align: right; }
.template-name {
  display: block;
  width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rowBorder {
  border-bottom: 1px solid var(--background-primary);
  padding-top: 15px;
  padding-bottom: 15px;
}
.rowBorder:first-child {
  padding-top: 5px;
}
.rowBorder:last-child {
  border-bottom: none;
}

/* Status dot */
.status {
  border-radius: 50%;
  width: 10px;
  height: 10px;
  display: inline-block;
  margin-right: 10px;
}
.secondary-reason {
  margin-top: 5px;
}

/* Action icons */
.actionIcons {
  width: 20px;
  height: 20px;
}
.actionFlexContainer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
}

/* Tooltips */
.tooltip_container {
  position: relative;
  display: inline-block;
}
.tooltip_container .tooltip_text {
  visibility: hidden;
  width: 80px;
  font-size: 13px;
  background-color: var(--black);
  color: var(--white);
  pointer-events: none;
  opacity: 0;
  transition: all 0.2s ease-out;
  text-align: center;
  padding: 5px;
  line-height: 1.5;
  border-radius: 5px;
  position: absolute;
  z-index: 1;
  bottom: 100%;
  left: 50%;
  margin-left: -40px;
}
.tooltip_container:hover .tooltip_text {
  visibility: visible;
  opacity: 1;
}
.tooltip_container .tooltip_text::after {
  content: " ";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: var(--black) transparent transparent transparent;
}

/* Popover items */
.listContainer {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}
.popover-item {
  background: none;
  border: none;
  font: inherit;
  font-size: 14px;
  padding: 10px 16px;
  text-align: left;
  cursor: pointer;
  color: var(--black);
  transition: background 0.2s;
}
.popover-item:hover {
  background: var(--background-primary);
}
.popover-item--danger {
  color: var(--orange);
}

/* Empty state */
.filterImg {
  max-width: 200px;
  width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
}
.centered-message-container {
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Pagination */
.sticky-pagination {
  position: sticky;
  bottom: 0;
  left: 0;
  z-index: 20;
  padding: 16px 0 16px 0;
  margin-right: 20px;
  display: flex;
  justify-content: flex-end;
}

/* Responsive */
.desktop-only {
  display: block;
}
.mobile-only {
  display: none;
}
@media only screen and (max-width: 760px) {
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
  .template-search-field {
    width: 100%;
  }
  .template-table-header,
  .template-row {
    grid-template-columns: 9fr 3fr;
  }
  .segment-info {
    padding: 10px 0px 20px 0px;
  }
  .rowBorder {
    padding-left: 20px;
  }
  .sticky-pagination {
    padding: 8px 0 0 0;
    margin-top: 12px;
    justify-content: center;
  }
}
</style>
